import { useEffect, useMemo, useState } from 'react'
import { createFileRoute, notFound } from '@tanstack/react-router'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faArrowRight, faChevronRight, faMagnifyingGlass } from '@fortawesome/free-solid-svg-icons'
import { faWhatsapp } from '@fortawesome/free-brands-svg-icons'
import { Footer, HeaderMiddle, Navbar, TopBar, WA_NUMBER } from '../components/SiteChrome'
import { BrandEasyOrder } from '../components/BrandEasyOrder'
import { BrandTrustSections, type TrustQuestion } from '../components/BrandTrustSections'
import { WhatsAppChannelSection } from '../components/WhatsAppChannelSection'
import { whatsAppCtaProps, trackSelectItem, trackViewItemList } from '../lib/analytics'
import {
  getProductCategoryName,
  getProductPrice,
  getProductPrimaryImage,
} from '../lib/dropify'
import type { StorefrontCategory, StorefrontProduct } from '../lib/dropify'
import { getBrand } from '../data/brands'
import { DEFAULT_OG_IMAGE, INDEXABLE_ROBOTS, absoluteUrl, brandTitle, buildBrandLandingSchema, buildBreadcrumbSchema, buildFaqSchema, truncate } from '../lib/seo'
import { listStorefrontCategoriesServer, listStorefrontProductsServer } from '../lib/storefront'
import { getBrandProofImagesServer } from '../lib/testimonials'

const PRODUCT_CARD_IMAGE_TRANSFORM = { width: 320, height: 320, smart: true, format: 'webp', quality: 58 } as const
const BRAND_OG_IMAGE_TRANSFORM = { width: 1200, height: 630, smart: true, format: 'webp', quality: 78 } as const
const BRAND_PAGE_SIZE = 12

export function buildBrandHubTrustQuestions(brandName: string, categoryNames: string[]): TrustQuestion[] {
  const categoryLabel = categoryNames.length > 0 ? categoryNames.join(' & ') : 'produk'
  return [
    {
      q: `Apakah stok ${brandName} bisa dicek sebelum memesan?`,
      a: `Bisa. Kirim nama atau foto ${categoryLabel} ${brandName}, jumlah yang dibutuhkan, dan kota tujuan. Admin akan mengecek stok serta pilihan yang tersedia.`,
    },
    {
      q: `Berapa minimum order ${brandName}?`,
      a: `Minimum order ${brandName} mengikuti kategori dan model ${categoryLabel}. Kirim jumlah serta kota tujuan agar admin dapat memeriksa satuan pembelian, harga grosir, ongkir, dan ketentuan campur ukuran atau warna.`,
    },
    {
      q: `Bisakah memesan sample ${brandName} dulu?`,
      a: `Ketersediaan sample ${categoryLabel} ${brandName}, isi, biaya, dan pengirimannya mengikuti model yang dipilih. Tanyakan melalui WhatsApp sebelum memesan sample.`,
    },
    {
      q: `Apakah bisa campur ukuran atau warna saat order ${brandName}?`,
      a: `Kemungkinan campur ukuran dan warna bergantung pada model serta stok ${categoryLabel} ${brandName}. Kirim pembagian yang Anda inginkan agar admin dapat memastikan komposisinya sebelum pembayaran.`,
    },
    {
      q: `Berapa lama pengiriman ${brandName} dari Surabaya?`,
      a: `Pesanan ${brandName} diproses dari Surabaya. Kirim kota atau kecamatan tujuan agar admin dapat memeriksa pilihan ekspedisi, ongkir, dan estimasi tiba sebelum pembayaran.`,
    },
  ]
}

export const Route = createFileRoute('/merek/$slug')({
  loader: async ({ params }) => {
    const brand = getBrand(params.slug)
    if (!brand) throw notFound()

    const [categories, response, proofImages] = await Promise.all([
      listStorefrontCategoriesServer().catch(() => []),
      listStorefrontProductsServer({ data: { search: brand.searchTerm, page: 1, per_page: BRAND_PAGE_SIZE } })
        .catch(() => ({ items: [], total: 0, page: 1, per_page: BRAND_PAGE_SIZE, total_pages: 1 })),
      getBrandProofImagesServer(),
    ])

    // The search API matches descriptions too; keep only products that carry
    // the brand in their NAME so the page never shows an off-brand item.
    const products = response.items.filter((product) =>
      product.name.toLowerCase().includes(brand.searchTerm),
    )

    return {
      brand,
      products,
      productTotal: response.total,
      hasMore: response.page < response.total_pages,
      relatedCategories: categories.filter((category) => brand.categorySlugs.includes(category.slug)),
      proofImages,
    }
  },
  head: ({ loaderData }) => {
    const brand = loaderData?.brand
    if (!brand) return { meta: [{ title: 'Merek | Dropify' }] }

    // Keyword-first title core ("Grosir Bra & CD Piaoli") targets the queries
    // buyers actually type; the meta description is hand-written per brand in
    // data/brands.ts (price-from + WhatsApp CTA) instead of an intro slice.
    const title = brandTitle(brand.seoTitle)
    const description = truncate(brand.metaDescription, 155)
    const image = loaderData?.products?.[0]
      ? getProductPrimaryImage(loaderData.products[0], BRAND_OG_IMAGE_TRANSFORM)
      : DEFAULT_OG_IMAGE
    return {
      meta: [
        { title },
        { name: 'description', content: description },
        { name: 'robots', content: INDEXABLE_ROBOTS },
        { property: 'og:title', content: title },
        { property: 'og:description', content: description },
        { property: 'og:url', content: absoluteUrl(`/merek/${brand.slug}`) },
        { property: 'og:locale', content: 'id_ID' },
        { property: 'og:image', content: image },
        { name: 'twitter:card', content: 'summary_large_image' },
        { name: 'twitter:title', content: title },
        { name: 'twitter:description', content: description },
        { name: 'twitter:image', content: image },
      ],
      links: [{ rel: 'canonical', href: absoluteUrl(`/merek/${brand.slug}`) }],
      scripts: [
        {
          type: 'application/ld+json',
          children: JSON.stringify(buildBrandLandingSchema({
            brandName: brand.name,
            pageName: brand.seoTitle,
            description,
            path: `/merek/${brand.slug}`,
          })),
        },
        {
          type: 'application/ld+json',
          children: JSON.stringify(buildBreadcrumbSchema([
            { name: 'Beranda', path: '/' },
            { name: 'Merek', path: '/merek' },
            { name: brand.name, path: `/merek/${brand.slug}` },
          ])),
        },
        {
          type: 'application/ld+json',
          children: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'ItemList',
            '@id': `${absoluteUrl(`/merek/${brand.slug}`)}#products`,
            name: `Produk ${brand.name} di Dropify`,
            itemListElement: (loaderData?.products ?? []).slice(0, 25).map((product, index) => ({
              '@type': 'ListItem',
              position: index + 1,
              name: product.name,
              url: absoluteUrl(`/produk/${product.slug || product.id}`),
            })),
          }),
        },
        ...(brand.faq && brand.faq.length > 0
          ? [
              {
                type: 'application/ld+json',
                children: JSON.stringify(buildFaqSchema(brand.faq)),
              },
            ]
          : []),
      ],
    }
  },
  component: BrandPage,
})

function BrandProductCard({ product, listName }: { product: StorefrontProduct; listName: string }) {
  const href = `/produk/${product.slug || product.id}`

  const selectItem = () =>
    trackSelectItem(
      {
        item_id: product.id,
        item_name: product.name,
        item_category: getProductCategoryName(product),
        price: getProductPrice(product),
      },
      listName,
    )

  return (
    <div className="group bg-white rounded-xl overflow-hidden border border-slate-100 hover:border-blue-200 hover:shadow-lg transition-all duration-200">
      <a href={href} onClick={selectItem} className="block relative overflow-hidden bg-slate-100" style={{ height: 260 }}>
        <img
          src={getProductPrimaryImage(product, PRODUCT_CARD_IMAGE_TRANSFORM)}
          alt={product.name}
          width="320"
          height="320"
          loading="lazy"
          decoding="async"
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
        />
      </a>
      <div className="p-4">
        <p className="text-xs text-blue-600 font-semibold uppercase tracking-wider mb-1">{getProductCategoryName(product)}</p>
        <a href={href}>
          <h3 className="text-sm font-semibold text-slate-800 mb-2 leading-snug hover:text-blue-600 transition-colors line-clamp-2">
            {product.name}
          </h3>
        </a>
        <a
          href={href}
          className="inline-flex items-center gap-1.5 px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold rounded-lg transition-colors w-full justify-center"
        >
          Lihat detail
        </a>
      </div>
    </div>
  )
}

function BrandCategoryChooser({
  brand,
  relatedCategories,
}: {
  brand: ReturnType<typeof getBrand>
  relatedCategories: StorefrontCategory[]
}) {
  if (!brand) return null

  const categoryLinks = relatedCategories
    .map((category) => {
      const lpHref = brand.lpByCategory?.[category.slug] ?? brand.lpHref
      return {
        category,
        href: lpHref ?? `/kategori/${category.slug}`,
        isBrandLp: Boolean(brand.lpByCategory?.[category.slug] || brand.lpHref),
      }
    })
    .filter((item) => item.isBrandLp)

  if (categoryLinks.length === 0) return null

  return (
    <section className="mb-10" aria-labelledby={`pilih-kategori-${brand.slug}`}>
      <h2 id={`pilih-kategori-${brand.slug}`} className="text-lg font-black tracking-tight text-slate-900 mb-4">
        Pilih lini produk {brand.name}
      </h2>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {categoryLinks.map(({ category, href }) => (
          <a
            key={category.slug}
            href={href}
            className="group flex items-center justify-between rounded-2xl border border-slate-200 bg-white p-5 transition-all hover:border-blue-300 hover:shadow-md"
          >
            <div>
              <p className="text-sm font-bold text-slate-900">
                {brand.slug === 'gekko' && category.slug === 'manshet-sarung-tangan'
                  ? 'Grosir sarung tangan Gekko Grip'
                  : `Grosir ${category.name} ${brand.name}`}
              </p>
              <p className="mt-1 text-xs text-slate-500">Lihat model, MOQ, dan cara order</p>
            </div>
            <FontAwesomeIcon
              icon={faArrowRight}
              className="h-4 w-4 text-blue-600 transition-transform group-hover:translate-x-0.5"
            />
          </a>
        ))}
      </div>
    </section>
  )
}

function BrandPage() {
  const {
    brand,
    products: initialProducts,
    productTotal: initialProductTotal,
    hasMore: initialHasMore,
    relatedCategories,
    proofImages,
  } = Route.useLoaderData()
  const listName = `merek_${brand.slug}`

  const [allProducts, setAllProducts] = useState(initialProducts)
  const [productTotal, setProductTotal] = useState(initialProductTotal)
  const [page, setPage] = useState(1)
  const [hasMore, setHasMore] = useState(initialHasMore)
  const [loadingMore, setLoadingMore] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')

  const filteredProducts = useMemo(() => {
    const query = searchQuery.trim().toLowerCase()
    if (!query) return allProducts
    return allProducts.filter((product) => product.name.toLowerCase().includes(query))
  }, [allProducts, searchQuery])

  useEffect(() => {
    setAllProducts(initialProducts)
    setProductTotal(initialProductTotal)
    setPage(1)
    setHasMore(initialHasMore)
    setSearchQuery('')
  }, [brand.slug, initialProducts, initialProductTotal, initialHasMore])

  useEffect(() => {
    if (filteredProducts.length > 0) {
      trackViewItemList(
        listName,
        filteredProducts.slice(0, 25).map((product) => ({
          item_id: product.id,
          item_name: product.name,
          item_category: getProductCategoryName(product),
          price: getProductPrice(product),
        })),
      )
    }
  }, [listName, filteredProducts])

  const handleLoadMore = () => {
    if (loadingMore || !hasMore) return

    const nextPage = page + 1
    setLoadingMore(true)
    listStorefrontProductsServer({ data: { search: brand.searchTerm, page: nextPage, per_page: BRAND_PAGE_SIZE } })
      .then((res) => {
        const nextProducts = res.items.filter((product) =>
          product.name.toLowerCase().includes(brand.searchTerm),
        )
        setAllProducts((prev) => {
          const seen = new Set(prev.map((p) => p.id))
          return [...prev, ...nextProducts.filter((p) => !seen.has(p.id))]
        })
        setPage(res.page)
        setProductTotal(res.total)
        setHasMore(res.page < res.total_pages)
      })
      .catch(() => setHasMore(false))
      .finally(() => setLoadingMore(false))
  }

  const waMessage = `Halo Dropify, saya mau tanya stok dan harga grosir produk ${brand.name}.`

  const noProductsAtAll = allProducts.length === 0 && !hasMore
  const searching = searchQuery.trim().length > 0

  return (
    <div className="min-h-screen bg-slate-50">
      <TopBar />
      <HeaderMiddle />
      <Navbar />

      <div className="bg-slate-900 py-14">
        <div className="max-w-7xl mx-auto px-4">
          <nav className="flex items-center gap-1.5 text-xs text-slate-400 mb-4">
            <a href="/" className="hover:text-white transition-colors">Beranda</a>
            <FontAwesomeIcon icon={faChevronRight} className="w-3 h-3" />
            <a href="/merek" className="hover:text-white transition-colors">Merek</a>
            <FontAwesomeIcon icon={faChevronRight} className="w-3 h-3" />
            <span className="text-white font-medium">{brand.name}</span>
          </nav>
          <h1 className="text-4xl font-black text-white tracking-tight mb-2">{brand.seoTitle}</h1>
          <p className="text-slate-300 text-sm max-w-2xl">{brand.intro}</p>
          <a
            href={`https://wa.me/${WA_NUMBER}?text=${encodeURIComponent(waMessage)}`}
            {...whatsAppCtaProps('brand', {
              cta_placement: 'hero_primary',
              lp_category: brand.name,
              lead_item_name: brand.name,
            })}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-5 inline-flex items-center gap-2.5 rounded-lg bg-green-700 px-6 py-3 text-sm font-bold text-white transition-colors hover:bg-green-800"
          >
            <FontAwesomeIcon icon={faWhatsapp} className="h-4 w-4" />
            Cek stok & harga {brand.name}
          </a>
        </div>
      </div>

        <BrandEasyOrder showNewsProof={false} />

      <WhatsAppChannelSection showSocksPromo={false} />

      <main className="max-w-7xl mx-auto px-4 py-10">
        <BrandCategoryChooser brand={brand} relatedCategories={relatedCategories} />

        {noProductsAtAll ? (
          <div className="rounded-2xl border border-slate-200 bg-white px-6 py-16 text-center">
            <p className="text-slate-500">
              {brand.lpHref
                ? `Produk ${brand.name} belum dapat ditampilkan di katalog ini. Buka halaman produknya atau kirim nama kategori yang Anda cari melalui WhatsApp.`
                : `Produk ${brand.name} belum dapat ditampilkan. Hubungi admin untuk meminta katalog dan informasi stok.`}
            </p>
            {brand.lpHref && (
              <a
                href={brand.lpHref}
                className="mt-5 inline-flex items-center gap-2 rounded-lg bg-blue-600 px-6 py-3 text-sm font-bold text-white transition-colors hover:bg-blue-700"
              >
                Lihat Halaman {brand.name}
              </a>
            )}
          </div>
        ) : (
          <>
            <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-sm text-slate-500">
                <span className="font-semibold text-slate-800">{filteredProducts.length}</span>
                {searching
                  ? ` produk ditemukan`
                  : productTotal > filteredProducts.length
                    ? ` dari ${productTotal} produk dimuat`
                    : ' produk dimuat'}
              </p>
              <div className="relative max-w-sm">
                <FontAwesomeIcon
                  icon={faMagnifyingGlass}
                  className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400"
                />
                <input
                  type="search"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder={`Cari produk ${brand.name}...`}
                  aria-label={`Cari produk ${brand.name}`}
                  className="w-full rounded-xl border border-slate-200 bg-white py-2.5 pl-10 pr-4 text-sm text-slate-700 placeholder:text-slate-400 focus:border-blue-400 focus:outline-none"
                />
              </div>
            </div>

            {filteredProducts.length === 0 ? (
              <div className="rounded-2xl border border-slate-200 bg-white px-6 py-16 text-center">
                <p className="text-slate-500">
                  Tidak ada produk yang cocok dengan pencarian "{searchQuery.trim()}".
                </p>
                {hasMore && (
                  <button
                    type="button"
                    onClick={handleLoadMore}
                    disabled={loadingMore}
                    className="mt-5 inline-flex items-center gap-2 rounded-lg border border-slate-300 px-6 py-3 text-sm font-semibold text-slate-700 transition-colors hover:border-slate-900 hover:text-slate-900 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {loadingMore ? 'Memuat produk...' : 'Muat lebih banyak produk'}
                  </button>
                )}
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4">
                {filteredProducts.map((product) => (
                  <BrandProductCard key={product.id} product={product} listName={listName} />
                ))}
              </div>
            )}

            {filteredProducts.length > 0 && (hasMore || loadingMore) && (
              <div className="mt-8 flex justify-center">
                <button
                  type="button"
                  onClick={handleLoadMore}
                  disabled={loadingMore}
                  className="inline-flex items-center justify-center rounded-lg border border-slate-300 px-6 py-3 text-sm font-semibold text-slate-700 transition-colors hover:border-slate-900 hover:text-slate-900 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {loadingMore ? 'Memuat produk...' : 'Tampilkan lainnya'}
                </button>
              </div>
            )}
          </>
        )}

        <div className="mt-10 rounded-2xl border border-slate-200 bg-white p-6">
          <p className="text-base leading-relaxed text-slate-600">{brand.detail}</p>
          {relatedCategories.length > 0 && (
            <p className="mt-3 text-sm text-slate-500">
              Lihat juga kategori terkait:{' '}
              {relatedCategories.map((category, i) => {
                const categoryHref = brand.lpByCategory?.[category.slug] ?? brand.lpHref ?? `/kategori/${category.slug}`
                return (
                  <span key={category.slug}>
                    {i > 0 && ', '}
                    <a href={categoryHref} className="font-semibold text-blue-600 hover:text-blue-700">
                      {brand.slug === 'gekko' && category.slug === 'manshet-sarung-tangan'
                        ? 'Grosir sarung tangan Gekko Grip'
                        : `Grosir ${category.name} ${brand.name}`}
                    </a>
                  </span>
                )
              })}
            </p>
          )}
        </div>

        <div className="-mx-4 mt-10">
          <BrandTrustSections brandName={brand.name} itemName={`produk ${brand.name}`} images={proofImages} questions={buildBrandHubTrustQuestions(brand.name, relatedCategories.map((category) => category.name))} />
        </div>

        {brand.faq && brand.faq.length > 0 && (
          <section className="mt-10">
            <h2 className="text-2xl font-black uppercase tracking-tight text-slate-900 mb-4">
              Pertanyaan Seputar Grosir {brand.name}
            </h2>
            <div className="space-y-3">
              {brand.faq.map((item) => (
                <div key={item.q} className="rounded-2xl border border-slate-200 bg-white p-6">
                  <h3 className="text-sm font-bold text-slate-800 mb-2">{item.q}</h3>
                  <p className="text-sm leading-relaxed text-slate-600">{item.a}</p>
                </div>
              ))}
            </div>
          </section>
        )}
      </main>

      <Footer />
    </div>
  )
}
